import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { Entity } from '../../classes/entity.class';
import { Utils } from '../../utils.class';
import { ApiResponse, ApiService } from '../api/api.service';
import { Features } from '../auth/features';
import { RealtimeService } from '../realtime/realtime.service';
import { WebsocketService } from '../websocket/websocket.service';
import { EntityType } from './entity-type.class';

@Injectable({ providedIn: 'root' })
export class EntitiesService extends RealtimeService<Entity> {
    private _types$: Observable<ApiResponse<EntityType[]>>;

    constructor(
        protected apiService: ApiService,
        protected websocketService: WebsocketService
    ) {
        super(websocketService, Features.Entities);
    }

    public itemInDaterange(entity: Entity | number, fromDate: Date, toDate: Date): boolean {
        const resolvedEntity = typeof entity === 'number' ? this.getItems().find(item => item.id === entity) : entity;

        return !!resolvedEntity && Utils.entityInDaterange(resolvedEntity, fromDate, toDate);
    }

    public get(id: number): Promise<Entity> {
        return this.apiService.getCall(`${this.type}/${id}`);
    }

    public getByType(
        code: string | string[],
        dates?: { begindate: Date; enddate: Date },
        withOutOfServiceEntityIds: number[] = []
    ): Observable<Entity[]> {
        return this.getList(dates?.begindate, dates?.enddate, withOutOfServiceEntityIds).pipe(
            map(entities => {
                const codes = Array.isArray(code) ? code.map(String) : [code];

                return entities.filter(entity => entity.entitytypes.some(type => codes.includes(type.key)));
            })
        );
    }

    public getList(beginDate?: Date, endDate?: Date, withOutOfServiceEntityIds: number[] = []): Observable<Entity[]> {
        return combineLatest([super.getList(), this.getTypesMap()])
            .pipe(
                map(([list, typeMap]) => {
                    list = list.map(entity => this.applyEntityTypes({ ...entity, outOfService: false }, typeMap));

                    if (beginDate) {
                        list = list.filter(entity => {
                            const notOut = this.itemInDaterange(
                                entity,
                                new Date(beginDate),
                                new Date(endDate ?? beginDate)
                            );

                            entity.outOfService = withOutOfServiceEntityIds?.includes(entity.id) && !notOut;

                            return withOutOfServiceEntityIds?.includes(entity.id) || notOut;
                        });

                        return list;
                    }

                    list = list.sort((a, b) => {
                        const getSorter = (entity: Entity) =>
                            Math.max(...entity.entitytypes.map(et => +(et.pivot?.order ?? 99999)));

                        return getSorter(a) - getSorter(b);
                    });

                    return list.filter(l => l.entitytypes?.length > 0);
                })
            )
            .pipe(
                map(a =>
                    a.sort((a, b) => {
                        const getSorter = (entity: Entity) => (entity.use_once ? 0 : 1000);

                        return getSorter(a) - getSorter(b);
                    })
                )
            );
    }

    public getMap(): Observable<Map<number, Entity>> {
        return this.getList().pipe(map(entities => this.genEntitiesMap(entities)));
    }

    public getUnavailableEntityIds(planningId: number, beginDate: Date, endDate: Date): Promise<number[]> {
        return this.apiService.postCall(`${this.type}/not-available`, { planningId, beginDate, endDate });
    }

    public save(entity: Entity): Promise<Entity> {
        return this.apiService.postCall<Entity>(this.type, entity);
    }

    public changeOrder(entities: { order; entity_id; entitytype_key }[], entitytype_key: string): Promise<boolean> {
        return this.apiService.postCall(`${this.type}/change-order`, {
            entities,
            entitytype_key,
        });
    }

    public getTypes() {
        return (this._types$ ??= this.apiService.getCall$<EntityType[]>('entities/types').pipe(shareReplay()));
    }

    public getEntityTypeIcon(entityTypeKey: string, entityTypes: EntityType[]): string {
        return entityTypes.find(entityType => entityType.key === entityTypeKey)?.icon;
    }

    public getTypesMap(): Observable<Map<string, EntityType>> {
        return this.getTypes().pipe(
            map(types => {
                const typeMap = new Map<string, EntityType>();
                types.data.forEach(type => {
                    typeMap.set(type.key, type);
                });
                return typeMap;
            })
        );
    }

    private genEntitiesMap(entities: Entity[]): Map<number, Entity> {
        const map = new Map<number, Entity>();
        entities.forEach(entity => {
            map.set(entity.id, entity);
        });
        return map;
    }

    private applyEntityTypes(entity: Entity, typeMap: Map<string, EntityType>): Entity {
        const keys = entity.entitytype_keys ?? entity.entitytypes?.map(entityType => String(entityType.key)) ?? [];

        entity.entitytypes = keys
            .map(key => {
                const type = typeMap.get(key);

                const entityType: EntityType | null = type
                    ? {
                          ...type,
                          pivot: {
                              order: entity.entitytype_order?.[key] ?? type.pivot?.order ?? 99999,
                          },
                      }
                    : null;

                return entityType;
            })
            .filter((type): type is EntityType => !!type);

        return entity;
    }
}
